import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { useHttpClient } from '../../../shared/http-client';
import { toast } from 'react-toastify';
import { CustomerFormValues } from './types';
import dayjs from 'dayjs';
import { DatePicker } from '../../../elements/date-picker';

interface CustomerFormProps {
    item?: any;
    close: () => void;
    doDelete?: (item: any) => void;
    onAction?: () => void;
    isEdit: boolean;
}

export const CustomerForm = ({ item, close, doDelete, onAction, isEdit }: CustomerFormProps) => {
    const { t } = useTranslation() as { t: (key: string) => string };
    const { api, state } = useHttpClient();
    const [drivers, setDrivers] = useState<any | null>([]);
    const [areas, setAreas] = useState<any | null>([]);

    const initialData: CustomerFormValues = item ? {
        name: item.name || '',
        lat: item.lat,
        lon: item.lon,
        address: item.address || '',
        city: item.city || '',
        is_acquired: item.is_acquired || false,
        importance_level: item.importance_level || 'C',
        commercial: item.commercial || null,
        areas: Array.isArray(item.areas) && item.areas.length > 0 ? item.areas[0].id : item.areas || null,
        deactivated_at: item.deactivated_at || null,
        visit_frequency_days: item.visit_frequency_days || null
    } : {
        name: '',
        lat: undefined,
        lon: undefined,
        address: '',
        city: '',
        is_acquired: false,
        importance_level: 'C',
        commercial: null,
        areas: null,
        deactivated_at: null,
        visit_frequency_days: null
    };

    const [formData, setFormData] = useState<CustomerFormValues>(initialData);

    useEffect(() => {
        if (item) {
            setFormData({
                name: item.name || '',
                lat: item.lat,
                lon: item.lon,
                address: item.address || '',
                city: item.city || '',
                is_acquired: item.is_acquired || false,
                importance_level: item.importance_level || 'C',
                commercial: item.commercial || null,
                areas: Array.isArray(item.areas) && item.areas.length > 0 ? item.areas[0].id : item.areas || null,
                deactivated_at: item.deactivated_at || null,
                visit_frequency_days: item.visit_frequency_days || null
            });
        }
    }, [item]);

    useEffect(() => {
        api('/fleet/drivers/?is_commercial=true', 'GET');
    }, []);

    // Handle API response
    useEffect(() => {
        if (state.data && !state.loading && state.path?.includes('/fleet/customers/')) {
            if (state.method === 'POST' || state.method === 'PATCH') {
                toast.success(t('common.message.saved'));
                close();
            }
            if (state.method === 'DELETE') {
                toast.success(t('common.message.deleted'));
                close();
            }
        }
        if (state.data && !state.loading && state.path?.includes('/fleet/drivers/')) {
            setDrivers(state.data.results);
            api('/fleet/areas/', 'GET');
        }
        if (state.data && !state.loading && state.path?.includes('/fleet/areas/')) {
            setAreas(state.data.results);
        }

        if (state.error) {
            toast.error(t('common.message.error'));
        }
    }, [state, close, t]);

    const handleChange = (field: keyof CustomerFormValues, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (item?.id) {
            // Edit existing customer
            api(`/fleet/customers/${item.id}/`, 'PATCH', formData);
        } else {
            // Create new customer
            api('/fleet/customers/', 'POST', formData);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4 w-ful pt-4">
            <div className='flex gap-4 w-full justify-start'>
                <div className='w-1/3'>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.label.nombre')} *
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006bc6] focus:border-transparent"
                        placeholder={t('common.placeholder.nombre')}
                        required
                    />
                </div>
                <div className="flex items-center mt-[14px]">
                    <input
                        type="checkbox"
                        id="is_acquired"
                        checked={formData.is_acquired}
                        onChange={(e) => handleChange('is_acquired', e.target.checked)}
                        className="w-4 h-4 text-[#006bc6] border-gray-300 rounded focus:ring-[#006bc6]"
                    />
                    <label htmlFor="is_acquired" className="ml-2 text-sm font-medium text-gray-700">
                        {t('common.form-label.es-cliente')}
                    </label>
                </div>
                <div className='w-1/3'>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.label.comercial')}
                    </label>
                    <select
                        value={formData.commercial || ''}
                        onChange={(e) => handleChange('commercial', e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006bc6] focus:border-transparent"
                    >
                        <option value="">{t('Select')}</option>
                        {drivers.map((driver: any) => (
                            <option key={driver.id} value={driver.id}>
                                {driver.full_name || driver.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className='w-1/3'>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.label.fecha-baja')}
                    </label>
                    <input
                        type="date"
                        value={formData.deactivated_at || ''}
                        onChange={(e) => handleChange('deactivated_at', e.target.value || null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006bc6] focus:border-transparent"
                    />
                </div>

            </div>


            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('common.label.categoria')}
                </label>
                <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="radio"
                            name="importance_level"
                            value="A"
                            checked={formData.importance_level === 'A'}
                            onChange={(e) => handleChange('importance_level', e.target.value)}
                            className="w-4 h-4 text-[#006bc6] border-gray-300 focus:ring-[#006bc6]"
                        />
                        <span className="ml-2 text-sm text-gray-700">A</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="radio"
                            name="importance_level"
                            value="B"
                            checked={formData.importance_level === 'B'}
                            onChange={(e) => handleChange('importance_level', e.target.value)}
                            className="w-4 h-4 text-[#006bc6] border-gray-300 focus:ring-[#006bc6]"
                        />
                        <span className="ml-2 text-sm text-gray-700">B</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                        <input
                            type="radio"
                            name="importance_level"
                            value="C"
                            checked={formData.importance_level === 'C'}
                            onChange={(e) => handleChange('importance_level', e.target.value)}
                            className="w-4 h-4 text-[#006bc6] border-gray-300 focus:ring-[#006bc6]"
                        />
                        <span className="ml-2 text-sm text-gray-700">C</span>
                    </label>
                </div>
            </div>



            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.label.direccion')}
                    </label>
                    <input
                        type="text"
                        value={formData.address || ''}
                        onChange={(e) => handleChange('address', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006bc6] focus:border-transparent"
                        placeholder={t('common.placeholder.direccion')}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.label.poblacion')}
                    </label>
                    <input
                        type="text"
                        value={formData.city || ''}
                        onChange={(e) => handleChange('city', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006bc6] focus:border-transparent"
                        placeholder={t('common.label.poblacion')}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.label.area')}
                    </label>
                    <select
                        value={formData.areas || ''}
                        onChange={(e) => handleChange('areas', e.target.value ? parseInt(e.target.value) : null)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006bc6] focus:border-transparent"
                    >
                        <option value="">{t('Select')}</option>
                        {areas.map((area: any) => (
                            <option key={area.id} value={area.id}>
                                {area.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <p className="mt-1 text-xs text-gray-500">
                {t('common.label.geocoding-help')}
            </p>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.label.latitud')}
                    </label>
                    <input
                        type="number"
                        step="any"
                        value={formData.lat || ''}
                        onChange={(e) => handleChange('lat', e.target.value ? parseFloat(e.target.value) : undefined)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006bc6] focus:border-transparent"
                        min="-90"
                        max="90"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.label.longitud')}
                    </label>
                    <input
                        type="number"
                        step="any"
                        value={formData.lon || ''}
                        onChange={(e) => handleChange('lon', e.target.value ? parseFloat(e.target.value) : undefined)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006bc6] focus:border-transparent"
                        min="-180"
                        max="180"
                    />
                </div>
            </div>
            <div className="mt-4 w-[300px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('common.label.visitar-cada')}
                </label>
                <div className="flex items-center gap-2">
                    <input
                        type="number"
                        value={formData.visit_frequency_days || ''}
                        onChange={(e) => handleChange('visit_frequency_days', e.target.value ? parseInt(e.target.value) : null)}
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#006bc6] focus:border-transparent"
                        min="1"
                    />
                    <span className="text-sm text-gray-700">{t('common.label.dias')}</span>
                </div>
            </div>



            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <div className="flex gap-3">
                    <button
                        type="button"
                        onClick={close}
                        className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-all"
                    >
                        {t('common.button.cancel')}
                    </button>
                    <div>
                    {isEdit && item?.id && (
                        <button
                            type="button"
                            onClick={() => {
                                if (window.confirm(t('common.message.confirm-delete'))) {
                                    api(`/fleet/customers/${item.id}/`, 'DELETE');
                                }
                            }}
                            className="px-4 py-2 text-white bg-red-500 hover:bg-red-500 rounded-lg font-medium transition-all"
                        >
                            {t('common.button.delete')}
                        </button>
                    )}
                </div>
                    <button
                        type="submit"
                        className="px-4 py-2 bg-[#006bc6] hover:bg-[#0056a3] text-white rounded-lg font-medium transition-all"
                    >
                        {t('common.button.save')}
                    </button>
                </div>
            </div>
        </form>
    );
};
